import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'

const Button = forwardRef(({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  isLoading = false, 
  disabled = false, 
  className = '', 
  icon: Icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  ...props 
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  
  const variants = {
    primary: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500/20 shadow-lg hover:shadow-xl transform hover:scale-105',
    secondary: 'bg-secondary-700 hover:bg-secondary-600 text-white border border-secondary-600 focus:ring-secondary-500/20',
    accent: 'bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white focus:ring-accent-500/20 shadow-lg hover:shadow-xl transform hover:scale-105',
    outline: 'border-2 border-primary-500 text-primary-400 hover:bg-primary-500 hover:text-white focus:ring-primary-500/20',
    ghost: 'text-secondary-300 hover:text-white hover:bg-secondary-700 focus:ring-secondary-500/20',
    danger: 'bg-error-500 hover:bg-error-600 text-white focus:ring-error-500/20 shadow-lg hover:shadow-xl',
    success: 'bg-success-500 hover:bg-success-600 text-white focus:ring-success-500/20 shadow-lg hover:shadow-xl'
  }
  
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl'
  }
  
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7'
  }

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || isLoading}
      onClick={onClick}
      {...props}
    >
      {isLoading && (
        <Loader2 className={`${iconSizes[size]} animate-spin mr-2`} />
      )}
      
      {!isLoading && Icon && iconPosition === 'left' && (
        <Icon className={`${iconSizes[size]} mr-2`} />
      )}
      
      {children}
      
      {!isLoading && Icon && iconPosition === 'right' && (
        <Icon className={`${iconSizes[size]} ml-2`} />
      )}
    </button>
  )
})

Button.displayName = 'Button'

export default Button
